// MunicipalitySubsidyService.js
// Service for municipality-specific labels and subsidy eligibility logic

import { isEnergyLabelAtMost } from "@/helpers/EnergyLabel";

const municipalityConfigs = {
  default: {
    labels: {
      woz: "Wat is de WOZ-waarde van uw woning volgens de WOZ-beschikking?",
    },
    subsidies: [],
  },
  Pekela: {
    wozPeiljaar: 2023,
    labels: {
      woz: "Wat is de WOZ-waarde van uw woning volgens de beschikking?",
    },
    visiblePages: ["formWozWaarde", "formSubsidy"],
    subsidies: ["Isolatiemaatregelen"],
    subsidyTargetGroups: {
      "Isolatiemaatregelen": "nonVve",
    },
    subsidyTypes: {
      "Isolatiemaatregelen": "",
    },
    subsidyAmounts: {
      "Isolatiemaatregelen": { max: 1750 },
    },
    subsidyExecution: {
      "Isolatiemaatregelen": { application: "before" },
    },
    subsidyInfo: {
      "Isolatiemaatregelen": [
        "Aanvragen vooraf, uiterlijk 4 weken voor de start.",
        "Start niet met de werkzaamheden voordat de aanvraag is gedaan.",
        "Bewijs: aanvraag/offerte en na uitvoering facturen of andere betaalbewijzen.",
        "Uitvoering binnen 1 jaar, volgens de regeling.",
      ],
    },
  },
  "Midden-Groningen": {
    wozPeiljaar: 2023,
    labels: {
      woz: "Wat is de WOZ-waarde van uw woning volgens de beschikking?",
    },
    subsidies: [],
  },
  Veendam: {
    wozPeiljaar: 2023,
    labels: {
      woz: "Wat is de WOZ-waarde van uw woning volgens de beschikking?",
    },
    visiblePages: ["formWozWaarde", "formSubsidy"],
    subsidies: ["Verduurzaming & isolatie woningen"],
    subsidyTargetGroups: {
      "Verduurzaming & isolatie woningen": "nonVve",
    },
    subsidyTypes: {
      "Verduurzaming & isolatie woningen": "",
    },
    subsidyAmounts: {
      "Verduurzaming & isolatie woningen": { max: 2225 },
    },
    subsidyExecution: {
      "Verduurzaming & isolatie woningen": { application: "before" },
    },
    subsidyInfo: {
      "Verduurzaming & isolatie woningen": [
        "Aanvragen vooraf, uiterlijk 4 weken voor de uitvoering.",
        "Start niet met de werkzaamheden voordat de aanvraag is gedaan.",
        "Doe-het-zelfmaatregelen zijn toegestaan.",
      ],
    },
  },
  Stadskanaal: {
    wozPeiljaar: 2023,
    labels: {
      woz: "Wat is de WOZ-waarde van uw woning volgens de beschikking?",
    },
    visiblePages: ["formWozWaarde", "formSubsidy"],
    subsidies: ["Lokale aanpak isolatie", "Energiebesparende maatregelen lage inkomens"],
    subsidyTargetGroups: {
      "Lokale aanpak isolatie": "nonVve",
      "Energiebesparende maatregelen lage inkomens": "nonVve",
    },
    subsidyTypes: {
      "Lokale aanpak isolatie": "",
      "Energiebesparende maatregelen lage inkomens": "",
    },
    subsidyAmounts: {
      "Lokale aanpak isolatie": { max: 2000 },
      "Energiebesparende maatregelen lage inkomens": { max: 3500 },
    },
    subsidyExecution: {
      "Lokale aanpak isolatie": { application: "after" },
      "Energiebesparende maatregelen lage inkomens": { application: "before" },
    },
    subsidyInfo: {
      "Lokale aanpak isolatie": [
        "Aanvragen achteraf, na uitvoering van de maatregelen.",
        "Bewijs: aanvraag en factuur, met eventueel aanvullende bewijsstukken.",
      ],
      "Energiebesparende maatregelen lage inkomens": [
        "Aanvragen vooraf; de maatregelen mogen nog niet zijn uitgevoerd.",
        "Let op: de actuele status van deze regeling moet bij de gemeente worden gecontroleerd.",
      ],
    },
  },
  Oldambt: {
    wozPeiljaar: 2023,
    labels: {
      woz: "Wat is de WOZ-waarde van uw woning volgens de beschikking?",
    },
    visiblePages: ["formWozWaarde", "formSubsidy"],
    subsidies: ["Isolatiemaatregelen"],
    subsidyTargetGroups: {
      "Isolatiemaatregelen": "nonVve",
    },
    subsidyTypes: {
      "Isolatiemaatregelen": "",
    },
    subsidyAmounts: {
      "Isolatiemaatregelen": { max: 1250 },
    },
    subsidyExecution: {
      "Isolatiemaatregelen": { application: "before" },
    },
    subsidyInfo: {
      "Isolatiemaatregelen": [
        "Aanvragen vooraf; start niet met de werkzaamheden voordat de aanvraag is gedaan.",
        "Bij de aanvraag is een ondertekende offerte nodig.",
        "Na uitvoering zijn facturen en betaalbewijzen nodig; controle kan onderdeel zijn van de verantwoording.",
        "Uitvoering binnen 1 jaar, volgens de regeling.",
      ],
    },
  },
  Westerwolde: {
    wozPeiljaar: 2023,
    labels: {
      woz: "Wat is de WOZ-waarde van uw woning volgens de beschikking?",
    },
    visiblePages: ["formSubsidy"],
    subsidies: ["GOUD Isoleert"],
    subsidyTargetGroups: {
      "GOUD Isoleert": "nonVve",
    },
    subsidyTypes: {
      "GOUD Isoleert": "",
    },
    subsidyAmounts: {
      "GOUD Isoleert": {
        incomeBased: {
          atMost140Pct: 5000,
          above140Pct: 1900,
        },
      },
    },
    subsidyExecution: {
      "GOUD Isoleert": { twkFrom: "2023-12-19" },
    },
    subsidyInfo: {
      "GOUD Isoleert": [
        "Vraag offertes aan en doe de aanvraag zodra de offertes binnen zijn.",
        "Terugwerkende kracht wordt genoemd tot en met 19 december 2023.",
      ],
    },
    executionDateFrom: "2023-12-19",
  },
};

const SUBSIDY_RULES = {
  "Isolatiemaatregelen": {
    Pekela: {
      address: ["woz<=249000"],
      ownership: ["isOwner", "OwnerIsResident"],
      wozResident: ["wozVolgensBewoner<=249000"],
      property: ["labelDof2Bouwdelen"],
      final: ["executionEligible"],
    },
    Oldambt: {
      address: ["woz<=239000"],
      ownership: ["isOwner", "OwnerIsResident"],
      wozResident: ["wozVolgensBewoner<=239000"],
      property: ["labelDof2Bouwdelen"],
      final: ["executionEligible"],
    },
  },
  "Verduurzaming & isolatie woningen": {
    Veendam: {
      address: ["woz<=429300"],
      ownership: ["isOwner", "OwnerIsResident"],
      wozResident: ["wozVolgensBewoner<=429300"],
      property: ["labelDof2Bouwdelen"],
      final: ["executionEligible"],
    },
  },
  "Lokale aanpak isolatie": {
    Stadskanaal: {
      address: ["woz<=441000"],
      ownership: ["isOwner", "OwnerIsResident"],
      wozResident: ["wozVolgensBewoner<=441000"],
      property: ["labelDof2Bouwdelen"],
      final: ["executionEligible"],
    },
  },
  "Energiebesparende maatregelen lage inkomens": {
    Stadskanaal: {
      address: ["bouwjaar<=1995"],
      ownership: ["isOwner", "OwnerIsResident"],
      financing: ["inkomen140pct"],
      final: ["executionEligible"],
    },
  },
  "GOUD Isoleert": {
    Westerwolde: {
      address: [],
      ownership: ["isOwner"],
      property: ["labelDof2Bouwdelen"],
      final: ["executionEligible"],
    },
  },
};

const PHASES = ["address", "ownership", "financing", "wozResident", "property", "financingVhf" ,"final"];

function formatEuro(value) {
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function getEffectiveConstructionYear(leadStore) {
  const bagYear = Number.parseInt(leadStore.address?.BAG_oorspronkelijkBouwjaar__c, 10);
  const userYear = Number.parseInt(leadStore.property?.ConstructionYear, 10);

  if (Number.isFinite(userYear) && (!Number.isFinite(bagYear) || userYear !== bagYear)) {
    return userYear;
  }

  return Number.isFinite(bagYear) ? bagYear : null;
}

function parsePositiveNumber(value) {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "string" && value.trim() === "") {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

function getWozValueWithSource(leadStore) {
  const municipalWoz = parsePositiveNumber(leadStore.address?.woz_waarde_ozb);
  if (municipalWoz !== null) {
    return { value: municipalWoz, source: "gemeente" };
  }

  return { value: null, source: null };
}

function getRuleFailureReason(rule, leadStore, subsidy = null) {
  if (!rule) {
    return "de voorwaarden voor deze subsidie niet worden gehaald";
  }

  if (rule.startsWith("bouwjaar<=")) {
    const max = parseInt(rule.split("<=")[1], 10);
    const value = getEffectiveConstructionYear(leadStore);

    if (!value) return `het bouwjaar van de woning niet binnen de grens tot en met ${max} valt`;
    return `het bouwjaar (${value}) hoger is dan toegestaan (maximaal ${max})`;
  }

  if (rule.startsWith("woz<=")) {
    const max = parseInt(rule.split("<=")[1], 10);
    const { value, source } = getWozValueWithSource(leadStore);
    if (!Number.isFinite(value)) {
      return "er geen WOZ-waarde van gemeente of bewoner bekend is";
    }
    return `de WOZ-waarde (${formatEuro(value)}${source ? `, bron: ${source}` : ""}) hoger is dan toegestaan (maximaal ${formatEuro(max)})`;
  }

  if (rule.startsWith("wozVolgensBewoner<=")) {
    const max = parseInt(rule.split("<=")[1], 10);
    const value = leadStore.otherSubsidies?.woz?.woz_waarde_volgens_bewoner;
    if (value === null || value === undefined) {
      return "de WOZ-waarde volgens bewoner ontbreekt";
    }
    return `de opgegeven WOZ-waarde (${formatEuro(value)}) hoger is dan toegestaan (maximaal ${formatEuro(max)})`;
  }

  if (rule.startsWith("energielabel<=")) {
    const max = rule.split("<=")[1];
    const value = leadStore.address?.Energielabel__c || "onbekend";
    return `het energielabel (${value}) niet voldoet aan de eis (${max} of lager)`;
  }

  if (rule.startsWith("postcode=[")) {
    return "de postcode niet binnen het subsidiegebied valt";
  }

  if (rule === "isOwner") {
    return "u geen eigenaar bent van de woning";
  }

  if (rule === "OwnerIsResident") {
    return "de eigenaar niet op dit adres woont";
  }

  if (rule === "inkomen140pct") {
    return "het inkomen boven 140% van het sociaal minimum ligt";
  }

  if (rule === "labelDof2Bouwdelen") {
    return "de woning geen energielabel D of lager heeft en er minder dan twee slecht geisoleerde bouwdelen zijn";
  }

  if (rule === "labelBof2Bouwdelen") {
    return "de woning geen energielabel B of lager heeft en er minder dan twee slecht geisoleerde bouwdelen zijn";
  }

  if (rule === "vhfIsolationStandardNeeded") {
    return "er geen maatregelen nodig zijn voor isolatiestandaard of voldoende labelsprongen";
  }

  if (rule === "vhfVeendamIncomeAtMost180") {
    return "het huishoudinkomen boven 180% ligt";
  }

  if (rule === "executionEligible") {
    const executionConfig = getSubsidyExecutionConfig(subsidy, leadStore);
    const executed = leadStore.otherSubsidies?.subsidy?.measuresExecuted === true;
    if (executionConfig?.application === "before" && executed) {
      return "de maatregelen al zijn uitgevoerd terwijl de aanvraag vooraf moest worden gedaan";
    }
    if (executionConfig?.application === "after" && !executed) {
      return "de maatregelen nog niet zijn uitgevoerd";
    }
    if (executionConfig?.twkFrom && executed) {
      return `de uitvoeringsdatum niet op of na ${executionConfig.twkFrom} ligt`;
    }
    return "de uitvoeringsvoorwaarden niet worden gehaald";
  }

  return "de voorwaarden voor deze subsidie niet worden gehaald";
}

function isTruthyFlag(value) {
  return value === true || value === "true" || value === 1 || value === "1";
}

function formatBoolNl(value) {
  if (value === true) return "ja";
  if (value === false) return "nee";
  return "onbekend";
}

function hasMeaningfulRequirementValue(value) {
  if (value === null || value === undefined) {
    return false;
  }

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  return normalized !== "onbekend" && normalized !== "niet van toepassing";
}

function getRuleRequirementDetail(rule, leadStore, subsidy) {
  const isMet = checkRule(rule, leadStore, subsidy);

  if (rule.startsWith("bouwjaar<=")) {
    const max = parseInt(rule.split("<=")[1], 10);
    const year = getEffectiveConstructionYear(leadStore);
    return {
      rule,
      label: `Bouwjaar maximaal ${max}`,
      currentValue: `${year || "onbekend"}`,
      isMet,
    };
  }

  if (rule.startsWith("woz<=")) {
    const max = parseInt(rule.split("<=")[1], 10);
    const { value, source } = getWozValueWithSource(leadStore);
    const current = Number.isFinite(value)
      ? `${formatEuro(value)}${source ? ` (${source})` : ""}`
      : "onbekend";
    return {
      rule,
      label: `WOZ-waarde maximaal ${formatEuro(max)}`,
      currentValue: current,
      isMet,
    };
  }

  if (rule.startsWith("wozVolgensBewoner<=")) {
    const max = parseInt(rule.split("<=")[1], 10);
    const municipalWoz = parsePositiveNumber(leadStore.address?.woz_waarde_ozb);

    // Only show the resident-specific WOZ row when it is actually relevant,
    // otherwise the generic WOZ rule already covers the check.
    if (municipalWoz !== null) {
      return null;
    }

    const residentWoz = parsePositiveNumber(leadStore.otherSubsidies?.woz?.woz_waarde_volgens_bewoner);
    return {
      rule,
      label: `WOZ-waarde volgens bewoner maximaal ${formatEuro(max)}`,
      currentValue: residentWoz !== null ? `${formatEuro(residentWoz)} (bewoner)` : "onbekend",
      isMet,
    };
  }

  if (rule.startsWith("energielabel<=")) {
    const max = rule.split("<=")[1];
    const rawLabel = leadStore.address?.Energielabel__c;
    const label = rawLabel || "onbekend";

    // Skip this row when the energy label is unknown, because this check passes
    // by design on missing values and is then less informative than follow-up checks.
    if (!rawLabel) {
      return null;
    }

    return {
      rule,
      label: `Energielabel ${max} of lager`,
      currentValue: label,
      isMet,
    };
  }

  if (rule.startsWith("postcode=[")) {
    const postalCode = (leadStore.address?.PostalCode || "onbekend").toUpperCase();
    return {
      rule,
      label: "Postcode valt binnen subsidiegebied",
      currentValue: postalCode,
      isMet,
    };
  }

  if (rule === "isOwner") {
    return {
      rule,
      label: "Aanvrager is eigenaar van de woning",
      currentValue: formatBoolNl(leadStore.ownership?.PropertyOwner),
      isMet,
    };
  }

  if (rule === "OwnerIsResident") {
    return {
      rule,
      label: "Aanvrager is ingeschreven op dit adres",
      currentValue: formatBoolNl(leadStore.ownership?.OwnerIsResident),
      isMet,
    };
  }

  if (rule === "inkomen140pct") {
    return {
      rule,
      label: "Inkomen maximaal 140% van sociaal minimum",
      currentValue: formatBoolNl(leadStore.financing?.income?.SM_140pct),
      isMet,
    };
  }

  if (rule === "labelDof2Bouwdelen") {
    const value = leadStore.otherSubsidies?.energyLabelDOrLowerOrTwoPoorInsulatedParts;
    return {
      rule,
      label: "Label D of lager, of minimaal 2 slecht geisoleerde bouwdelen",
      currentValue: formatBoolNl(value),
      isMet,
    };
  }

  if (rule === "labelBof2Bouwdelen") {
    const dOrTwoPoorParts = leadStore.otherSubsidies?.energyLabelDOrLowerOrTwoPoorInsulatedParts;
    const bOrLower = leadStore.otherSubsidies?.vhfEnergyLabelBOrLower;
    const current = dOrTwoPoorParts === true ? "ja (via label D of 2 bouwdelen)" : formatBoolNl(bOrLower);
    return {
      rule,
      label: "Label B of lager, of minimaal 2 slecht geisoleerde bouwdelen",
      currentValue: current,
      isMet,
    };
  }

  if (rule === "vhfIsolationStandardNeeded") {
    const value = leadStore.otherSubsidies?.vhfNeedsIsolationStandardMeasures;
    const current = value === "unknown" ? "onbekend" : formatBoolNl(value);
    return {
      rule,
      label: "Maatregelen nodig voor isolatiestandaard of labelsprongen",
      currentValue: current,
      isMet,
    };
  }

  if (rule === "vhfVeendamIncomeAtMost180") {
    const percentage = getVeendamVhfPercentage(leadStore);
    const current = percentage === null ? "onbekend" : `${percentage}%`;
    return {
      rule,
      label: "Inkomen geeft recht op VHF%",
      currentValue: current,
      isMet,
    };
  }

  if (rule === "executionEligible") {
    const executionConfig = getSubsidyExecutionConfig(subsidy, leadStore);
    const executed = leadStore.otherSubsidies?.subsidy?.measuresExecuted === true;
    let label = "Zijn de maatregelen al uitgevoerd";

    if (!MunicipalitySubsidyService.executionChecksEnabled) return null;

    if (executionConfig?.application === "before") {
      label = "Maatregelen nog niet uitgevoerd bij aanvraag";
    } else if (executionConfig?.application === "after") {
      label = "Maatregelen uitgevoerd bij aanvraag";
    } else if (executionConfig?.twkFrom) {
      label = `Maatregelen uitgevoerd op of na ${executionConfig.twkFrom}`;
    }

    return {
      rule,
      label,
      currentValue: formatBoolNl(executed),
      isMet,
    };
  }

  return {
    rule,
    label: "Overige voorwaarde",
    currentValue: rule,
    isMet,
  };
}

function getVeendamVhfPercentage(leadStore) {
  const income = leadStore.financing?.income || {};
  const sm140 = isTruthyFlag(income.SM_140pct);
  const sm160 = isTruthyFlag(income.SM_160pct);
  const sm180 = isTruthyFlag(income.SM_180pct);

  if (sm180) {
    return 25;
  }

  if (sm160) {
    return 50;
  }

  if (sm140 && !sm160 && !sm180) {
    return 100;
  }

  if (income.SM_140pct === false && income.SM_160pct === false && income.SM_180pct === false) {
    return 0;
  }

  return null;
}

function getSubsidyAmountText(subsidy, leadStore) {
  const municipality = leadStore.address?.pc_GemNaam;
  const subsidyAmountConfig = municipalityConfigs?.[municipality]?.subsidyAmounts?.[subsidy];
  const maintenanceLarge = subsidyAmountConfig?.maintenance?.large ?? subsidyAmountConfig?.large;
  const maintenanceSmall = subsidyAmountConfig?.maintenance?.small ?? subsidyAmountConfig?.small;

  if (subsidy === "VHF" && municipality === "Veendam") {
    const percentage = getVeendamVhfPercentage(leadStore);

    if (percentage !== null) {
      return `${percentage}% subsidie op VHF`;
    }
  }

  if (subsidyAmountConfig?.incomeBased) {
    const { atMost140Pct, above140Pct } = subsidyAmountConfig.incomeBased;
    const isAtMost140Pct = leadStore.financing?.income?.SM_140pct;
    const hasIncomeCategory =
      leadStore.financing?.income?.SM_100pct ||
      leadStore.financing?.income?.SM_110pct ||
      leadStore.financing?.income?.SM_125pct ||
      leadStore.financing?.income?.SM_140pct ||
      leadStore.financing?.income?.SM_160pct ||
      leadStore.financing?.income?.SM_180pct ||
      leadStore.financing?.income?.WML_100pct ||
      leadStore.financing?.income?.WML_125pct ||
      leadStore.financing?.income?.WML_140pct ||
      leadStore.financing?.income?.over_WML_140pct;

    if (isAtMost140Pct === true && Number.isFinite(atMost140Pct)) {
      return `Maximaal ${formatEuro(atMost140Pct)} subsidie`;
    }

    if (isAtMost140Pct === false && Number.isFinite(above140Pct)) {
      return `Maximaal ${formatEuro(above140Pct)} subsidie`;
    }

    if (hasIncomeCategory && Number.isFinite(above140Pct)) {
      return `Maximaal ${formatEuro(above140Pct)} subsidie`;
    }

    if (Number.isFinite(atMost140Pct) && Number.isFinite(above140Pct)) {
      return `Maximaal ${formatEuro(atMost140Pct)} of ${formatEuro(above140Pct)} subsidie, afhankelijk van uw inkomen`;
    }
  }

  if (
    Number.isFinite(subsidyAmountConfig?.levensloopMax) &&
    Number.isFinite(maintenanceLarge) &&
    Number.isFinite(maintenanceSmall)
  ) {
    return `VHF Levensloop: tot ${formatEuro(subsidyAmountConfig.levensloopMax)}. Onderhoudsmaatregelen: groot onderhoud tot ${formatEuro(maintenanceLarge)}, klein onderhoud tot ${formatEuro(maintenanceSmall)}`;
  }

  if (Number.isFinite(maintenanceLarge) && Number.isFinite(maintenanceSmall)) {
    return `Groot onderhoud: tot ${formatEuro(maintenanceLarge)}. Klein onderhoud: tot ${formatEuro(maintenanceSmall)}`;
  }

  if (Number.isFinite(subsidyAmountConfig?.max)) {
    return `Maximaal ${formatEuro(subsidyAmountConfig.max)} `;
  }

  if (subsidy === "NIP") {
    const desc = leadStore.address?.gem_NIP_amount_desc_short;
    if (desc) {
      return `Maximaal ${desc}`;
    }

    const amount = Number(leadStore.address?.gem_NIP_subsidy_amount);
    if (Number.isFinite(amount) && amount > 0) {
      return `Maximaal ${formatEuro(amount)}`;
    }

    return "Bedrag verschilt per gemeente";
  }

  if (subsidy === "VHF") {
    if (municipality === "Veendam") {
      const percentage = getVeendamVhfPercentage(leadStore);
      if (percentage !== null) {
        return `${percentage}% subsidie op VHF`;
      }
    }

    return "Bedrag wordt door de gemeente bepaald";
  }

  return "Bedrag op aanvraag";
}

function getSubsidyType(subsidy, municipality) {
  const subsidyType = municipalityConfigs?.[municipality]?.subsidyTypes?.[subsidy];
  return subsidyType?.trim() || null;
}

function getSubsidyExecutionConfig(subsidy, leadStore) {
  const municipality = leadStore.address?.pc_GemNaam;
  return MunicipalitySubsidyService.getConfig(municipality).subsidyExecution?.[subsidy] || null;
}

function getTwkOutcome(subsidy, leadStore) {
  if (!MunicipalitySubsidyService.executionChecksEnabled) return null;
  const executionConfig = getSubsidyExecutionConfig(subsidy, leadStore);
  const executed = leadStore.otherSubsidies?.subsidy?.measuresExecuted === true;
  const isRetrospective = executionConfig?.application === "after" || Boolean(executionConfig?.twkFrom);

  if (!isRetrospective || !executed) {
    return null;
  }

  const twkEligibility = leadStore.otherSubsidies?.subsidy?.twkEligibility?.[subsidy];
  const executedAt = leadStore.otherSubsidies?.subsidy?.executedAt;
  const isEligible = twkEligibility !== undefined
    ? twkEligibility === true
    : executionConfig.application === "after"
      ? true
      : Boolean(executedAt) && executedAt >= executionConfig.twkFrom;

  return {
    isEligible,
    text: isEligible ? "TWK is mogelijk." : "TWK is niet mogelijk.",
  };
}

function evaluateSubsidyByRules(subsidy, leadStore, phase) {
  const municipality = leadStore.address?.pc_GemNaam;
  const rules = SUBSIDY_RULES?.[subsidy]?.[municipality];
  if (!rules) {
    return { isEligible: false, failedPhase: null, failedRule: null };
  }

  const phaseIdx = PHASES.indexOf(phase);
  if (phaseIdx === -1) {
    return { isEligible: false, failedPhase: null, failedRule: null };
  }

  for (let i = 0; i <= phaseIdx; i++) {
    const phaseName = PHASES[i];
    const phaseRules = rules[phaseName] || [];

    for (const rule of phaseRules) {
      if (!checkRule(rule, leadStore, subsidy)) {
        return { isEligible: false, failedPhase: phaseName, failedRule: rule };
      }
    }
  }

  return { isEligible: true, failedPhase: null, failedRule: null };
}

function hasChecksAfterAddress(subsidy, municipality) {
  const rules = SUBSIDY_RULES?.[subsidy]?.[municipality];
  if (!rules) return false;

  return PHASES.slice(1).some((phaseName) => (rules[phaseName] || []).length > 0);
}

function checkRule(rule, leadStore, subsidy = null) {
  if (rule.startsWith("bouwjaar<=")) {
    const val = parseInt(rule.split("<=")[1], 10);
    return (getEffectiveConstructionYear(leadStore) || 0) <= val;
  }
  if (rule.startsWith("woz<=")) {
    const val = parseInt(rule.split("<=")[1], 10);
    const { value } = getWozValueWithSource(leadStore);
    if (!Number.isFinite(value)) {
      return true;
    }

    return value <= val;
  }
  if (rule.startsWith("postcode=[")) {
    const allowed = JSON.parse(rule.split("=")[1]);
    const normalizedPostalCode = (leadStore.address?.PostalCode || "")
      .replace(/\s+/g, "")
      .toUpperCase();
    const normalizedAllowed = Array.isArray(allowed)
      ? allowed.map((value) => String(value).replace(/\s+/g, "").toUpperCase())
      : [];

    return normalizedAllowed.some((allowedPostcode) => {
      //If postcode LIKE 9723, allows 9723JE, 9723JB
      if (allowedPostcode.length <= 4) {
        return normalizedPostalCode.substring(0, 4) === allowedPostcode;
      }
      //For specific postcodes, require an exact match (e.g. 9723JE).
      return normalizedPostalCode === allowedPostcode;
    });
  }
  if (rule.startsWith("energielabel<=")) {
    const val = rule.split("<=")[1];
    return isEnergyLabelAtMost(leadStore.address?.Energielabel__c, val, { missingAsPass: true });
  }
  if (rule === "isOwner") {
    return leadStore.ownership?.PropertyOwner === true;
  }
  if (rule === "OwnerIsResident") {
    return leadStore.ownership?.OwnerIsResident === true;
  }
  if (rule === "inkomen140pct") {
    return leadStore.financing?.income?.SM_140pct === true;
  }
  if (rule === "labelDof2Bouwdelen") {
    const answer = leadStore.otherSubsidies?.energyLabelDOrLowerOrTwoPoorInsulatedParts;
    if (answer === null || answer === undefined) {
      return true;
    }

    return answer === true;
  }
  if (rule === "labelBof2Bouwdelen") {
    const dOrTwoPoorParts = leadStore.otherSubsidies?.energyLabelDOrLowerOrTwoPoorInsulatedParts;

    if (dOrTwoPoorParts === true) {
      return true;
    }

    if (dOrTwoPoorParts === null || dOrTwoPoorParts === undefined) {
      return true;
    }

    const answer = leadStore.otherSubsidies?.vhfEnergyLabelBOrLower;
    if (answer === null || answer === undefined) {
      return true;
    }

    return answer === true || answer === "unknown";
  }
  if (rule === "vhfIsolationStandardNeeded") {
    const answer = leadStore.otherSubsidies?.vhfNeedsIsolationStandardMeasures;
    if (answer === null || answer === undefined) {
      return true;
    }

    return answer === true || answer === "unknown";
  }
  if (rule === "vhfVeendamIncomeAtMost180") {
    const percentage = getVeendamVhfPercentage(leadStore);

    if (percentage === null) {
      return true;
    }

    return percentage > 0;
  }
  if (rule === "executionEligible") {
    const executionConfig = getSubsidyExecutionConfig(subsidy, leadStore);
    const executed = leadStore.otherSubsidies?.subsidy?.measuresExecuted === true;

    if (!MunicipalitySubsidyService.executionChecksEnabled) return true;

    if (executionConfig?.application === "before") {
      return !executed;
    }

    if (executionConfig?.application === "after") {
      return executed;
    }

    if (executionConfig?.twkFrom && executed) {
      const twkEligibility = leadStore.otherSubsidies?.subsidy?.twkEligibility?.[subsidy];
      if (twkEligibility !== undefined) {
        return twkEligibility === true;
      }

      const executedAt = leadStore.otherSubsidies?.subsidy?.executedAt;
      return Boolean(executedAt) && executedAt >= executionConfig.twkFrom;
    }
  }
  if (rule.startsWith("wozVolgensBewoner<=")) {
    const val = parseInt(rule.split("<=")[1], 10);
    const municipalWoz = parsePositiveNumber(leadStore.address?.woz_waarde_ozb);
    if (municipalWoz !== null) {
      return true;
    }

    const residentWoz = parsePositiveNumber(leadStore.otherSubsidies?.woz?.woz_waarde_volgens_bewoner);
    if (residentWoz === null) {
      return false;
    }

    return residentWoz <= val;
  }

  return true;
}

function isEligibleByRules(subsidy, leadStore, phase) {
  return evaluateSubsidyByRules(subsidy, leadStore, phase).isEligible;
}

function getApplicableSubsidies(leadStore) {
  const config = MunicipalitySubsidyService.getConfig(leadStore.address?.pc_GemNaam);
  const targetGroup = leadStore.ownership?.HoaMember === true ? "vve" : "nonVve";
  const subsidies = Array.isArray(config.subsidies) ? config.subsidies : [];

  // Target groups: "both" (beide), "vve" (alleen VvE), "nonVve" (alleen geen VvE).
  // Missing or invalid target groups are excluded from both groups.
  return subsidies.filter((subsidy) => {
    const subsidyTargetGroup = config.subsidyTargetGroups?.[subsidy];
    return subsidyTargetGroup === "both" || subsidyTargetGroup === targetGroup;
  });
}

// Maps pages to the eligibility phase used to evaluate subsidy rules.
const PAGE_PHASE_MAPPING = {
  formOwnership: "ownership",
  formAddress: "address",
  formFinancing: "financing",
  otherSubsidies: "financing",
  formWozWaarde: "wozResident",
  formSubsidyEnergyLabel: "address",
  formSubsidyVhf: "property",
  formSubsidy: "property",
  // Add more page-to-phase mappings here when needed.
};

export const MunicipalitySubsidyService = {
  // Enable to restore the municipal execution question and eligibility check.
  executionChecksEnabled: false,

  /**
   * Returns the list of subsidies this lead is eligible for in the given phase.
   * @param {object} leadStore
   * @param {string} phase
   * @returns {string[]} List of eligible subsidies
   */
  getEligibleSubsidies(leadStore, phase) {
    return getApplicableSubsidies(leadStore).filter((subsidy) =>
      isEligibleByRules(subsidy, leadStore, phase),
    );
  },

  /**
   * Returns subsidy outcomes for subsidies that still had a chance after address checks.
   * @param {object} leadStore
   * @returns {Array<{subsidy: string, isEligible: boolean, amountText: string, reason: string, failedPhase: string|null, failedRule: string|null}>}
   */
  getPostAddressSubsidyOutcomes(leadStore, phase = "wozResident") {
    if (!leadStore?.address) return [];

    const municipality = leadStore.address.pc_GemNaam;

    return getApplicableSubsidies(leadStore)
      .filter((subsidy) => hasChecksAfterAddress(subsidy, municipality))
      .filter(
        (subsidy) => evaluateSubsidyByRules(subsidy, leadStore, "address").isEligible,
      )
      .map((subsidy) => {
        const result = evaluateSubsidyByRules(subsidy, leadStore, phase);
        const twkOutcome = getTwkOutcome(subsidy, leadStore);
        const isEligible = result.isEligible;
        const reason = result.isEligible ? "" : getRuleFailureReason(result.failedRule, leadStore, subsidy);

        return {
          subsidy,
          subsidyType: getSubsidyType(subsidy, municipality),
          isEligible,
          amountText: isEligible ? getSubsidyAmountText(subsidy, leadStore) : "",
          reason,
          twk: twkOutcome,
          failedPhase: result.failedPhase,
          failedRule: result.failedRule,
        };
      });
  },

  /**
   * Returns outcomes for all subsidies matching the lead's municipality and VvE status.
   * Includes both eligible and ineligible results with a failure reason.
   * @param {object} leadStore
   * @param {string} [phase='final']
   * @returns {Array<{subsidy: string, isEligible: boolean, amountText: string, reason: string, twk: object|null, failedPhase: string|null, failedRule: string|null}>}
   */
  getAllSubsidyOutcomes(leadStore, phase = "final") {
    if (!leadStore?.address) return [];

    const municipality = leadStore.address.pc_GemNaam;

    return getApplicableSubsidies(leadStore).map((subsidy) => {
      const result = evaluateSubsidyByRules(subsidy, leadStore, phase);
      const twkOutcome = getTwkOutcome(subsidy, leadStore);
      const isEligible = result.isEligible;
      const reason = isEligible ? "" : getRuleFailureReason(result.failedRule, leadStore, subsidy);

      return {
        subsidy,
        subsidyType: getSubsidyType(subsidy, municipality),
        isEligible,
        amountText: isEligible ? getSubsidyAmountText(subsidy, leadStore) : "",
        reason,
        twk: twkOutcome,
        failedPhase: result.failedPhase,
        failedRule: result.failedRule,
      };
    });
  },

  /**
  * Returns requirement details including current values and pass/fail per rule.
   * @param {string} subsidy
   * @param {object} leadStore
  * @param {string} [phase='final']
  * @param {string|null} [stopAtRule=null]
  * @returns {Array<{rule: string, label: string, currentValue: string, isMet: boolean}>}
   */
  getRequirementDetails(subsidy, leadStore, phase = "final", stopAtRule = null) {
    if (!leadStore?.address) return [];

    const municipality = leadStore.address.pc_GemNaam;
    const rulesByPhase = SUBSIDY_RULES?.[subsidy]?.[municipality];
    if (!rulesByPhase) return [];

    const phaseIdx = PHASES.indexOf(phase);
    const lastPhaseIdx = phaseIdx === -1 ? PHASES.length - 1 : phaseIdx;

    const requirements = [];
    let shouldStop = false;
    for (let i = 0; i <= lastPhaseIdx; i++) {
      const phaseName = PHASES[i];
      const phaseRules = rulesByPhase[phaseName] || [];

      for (const rule of phaseRules) {
        const detail = getRuleRequirementDetail(rule, leadStore, subsidy);
        const shouldShowDetail =
          detail &&
          (hasMeaningfulRequirementValue(detail.currentValue) || (stopAtRule && rule === stopAtRule));

        if (shouldShowDetail) {
          requirements.push(detail);
        }

        if (stopAtRule && rule === stopAtRule) {
          shouldStop = true;
          break;
        }
      }

      if (shouldStop) {
        break;
      }
    }

    return requirements;
  },

  /**
   * Returns the municipality config, with a fallback to the default config.
   * @param {string} municipality
   * @returns {object}
   */
  getConfig(municipality) {
    if (municipality && municipalityConfigs[municipality]) {
      return municipalityConfigs[municipality];
    }
    return municipalityConfigs.default;
  },

  /**
   * Returns a label for a given key and municipality.
   * @param {string} municipality
   * @param {string} labelKey
   * @returns {string}
   */
  getLabel(municipality, labelKey) {
    const config = this.getConfig(municipality);
    return (
      config.labels[labelKey] ||
      municipalityConfigs.default.labels[labelKey] ||
      ""
    );
  },

  getWozPeiljaar(municipality) {
    return this.getConfig(municipality).wozPeiljaar || null;
  },

  getSubsidyInfo(subsidy, municipality) {
    const notes = this.getConfig(municipality).subsidyInfo?.[subsidy] || [];
    return !this.executionChecksEnabled && notes.length
      ? [...notes, "Het aanvraagmoment en de uitvoeringsdatum zijn niet gecontroleerd. Controleer deze voorwaarden voordat u subsidie aanvraagt."]
      : notes;
  },

  getSubsidyAmount(subsidy, leadStore) {
    return getSubsidyAmountText(subsidy, leadStore);
  },

  getExecutionConfig(subsidy, leadStore) {
    return getSubsidyExecutionConfig(subsidy, leadStore);
  },


  /**
   * Checks whether subsidies are available for the lead in a specific phase.
   * @param {object} leadStore
   * @param {string} [phaseOverride='ownership']
   * @returns {boolean}
   */
  hasSubsidies(leadStore, phaseOverride = "ownership") {
    const municipality = leadStore.address?.pc_GemNaam;

    const config = this.getConfig(municipality);
    if (!Array.isArray(config.subsidies) || config.subsidies.length === 0)
      return false;
    if (leadStore) {
      return this.getEligibleSubsidies(leadStore, phaseOverride).length > 0;
    }
    return true;
  },

  /**
   * Checks whether a page should be visible for the lead,
   * using an optional page-specific eligibility phase.
   * @param {string} pageName
   * @param {object} leadStore
   * @param {string} [phaseOverride='ownership']
   * @param {string|null} [subsidyOverride=null]
   * @returns {boolean}
   */
  isPageVisible(pageName, leadStore, phaseOverride = "financing", subsidyOverride = null) {
    if (!this.executionChecksEnabled && ["formSubsidy", "formSubsidyVhf"].includes(pageName)) return false;
    const municipality = leadStore.address.pc_GemNaam;

    const config = this.getConfig(municipality);
    const applicableSubsidies = getApplicableSubsidies(leadStore);
    if (!this.hasSubsidies(leadStore, phaseOverride)) {
      return false;
    }

    // Determine the eligibility phase for this page.
    const phase = phaseOverride || PAGE_PHASE_MAPPING[pageName];
    const pageSubsidy = subsidyOverride || null;

    if (pageSubsidy) {
      if (!applicableSubsidies.includes(pageSubsidy)) {
        return false;
      }

      return isEligibleByRules(pageSubsidy, leadStore, phase) && config.visiblePages.includes(pageName);
    }

    if (
      leadStore &&
      applicableSubsidies.length > 0
    ) {
      const eligible = applicableSubsidies.filter((subsidy) =>
        isEligibleByRules(subsidy, leadStore, phase),
      );
      if (pageName === "otherSubsidies") {
        return eligible.length > 0;
      }
      // For other pages, only show if at least one subsidy is eligible in this phase.
      if (eligible.length === 0) return false;
    }
    return config.visiblePages.includes(pageName);
  },
}
